import { FC, ReactNode, useId } from 'react'
import clsx from 'clsx'
import { IconPaths, KTSVG } from '../../../_metronic/helpers'
import styles from './styles/DetailPanel.module.css'

/*
 * Blocos do painel de detalhe (linha expandida da tabela, painéis de info). O layout, o
 * carregamento e a acessibilidade ficam aqui; cada tela só compõe os campos que tem.
 *
 * @example
 * <DetailPanel isLoading={loading} loadingLabel='Carregando detalhes do exame…'>
 *   <DetailHeader title={exame.nome} meta={[exame.codigo && `Cód. ${exame.codigo}`, exame.tipo]} />
 *   <DetailFields>
 *     <DetailField label='Descrição' value={exame.descricao} wide />
 *   </DetailFields>
 *   <DetailFeatures label='Características'>
 *     <DetailFeature label='APAC' active={exame.apac} />
 *   </DetailFeatures>
 *   <DetailFooter>Cadastrado por <strong>{criador}</strong></DetailFooter>
 * </DetailPanel>
 */

type DetailPanelProps = {
  children?: ReactNode
  isLoading?: boolean
  /** Texto do leitor de tela enquanto carrega (ex.: "Carregando detalhes do exame…"). */
  loadingLabel?: string
}

export const DetailPanel: FC<DetailPanelProps> = ({
  children,
  isLoading,
  loadingLabel = 'Carregando detalhes…',
}) => {
  if (isLoading) {
    return (
      <div className={styles.loading} role='status'>
        <span className={styles.spinner} aria-hidden='true' />
        <span className='visually-hidden'>{loadingLabel}</span>
      </div>
    )
  }

  return <div className={styles.panel}>{children}</div>
}

export type DetailBadgeTone = 'neutral' | 'dark' | 'danger' | 'success'

type DetailHeaderProps = {
  title: ReactNode
  /** Linha de apoio abaixo do título; itens vazios são ignorados e o resto é unido por " · ". */
  meta?: Array<ReactNode | false | null | undefined>
  /** Etiqueta à direita (ex.: <DetailBadge>). */
  badge?: ReactNode
}

export const DetailHeader: FC<DetailHeaderProps> = ({ title, meta = [], badge }) => {
  const metaItems = meta.filter(Boolean)

  return (
    <div className={styles.header}>
      <div className={styles.titleGroup}>
        <span className={styles.title}>{title}</span>
        {metaItems.length > 0 && (
          <span className={styles.meta}>
            {metaItems.map((item, index) => (
              <span key={index}>
                {index > 0 && ' · '}
                {item}
              </span>
            ))}
          </span>
        )}
      </div>
      {badge}
    </div>
  )
}

/** Etiqueta com texto (o tom só reforça; o texto carrega o significado). */
export const DetailBadge: FC<{ children: ReactNode; tone?: DetailBadgeTone }> = ({
  children,
  tone = 'neutral',
}) => <span className={clsx(styles.badge, styles[`badge_${tone}`])}>{children}</span>

/** Subtítulo de um grupo de campos (ex.: "Linha do tempo"). */
export const DetailSection: FC<{ title: ReactNode; children: ReactNode }> = ({
  title,
  children,
}) => (
  <section className={styles.section}>
    <h4 className={styles.sectionTitle}>{title}</h4>
    {children}
  </section>
)

/** Grid de campos (`<dl>`): colunas de no mínimo 180px que quebram sozinhas. */
export const DetailFields: FC<{ children: ReactNode }> = ({ children }) => (
  <dl className={styles.fields}>{children}</dl>
)

type DetailFieldProps = {
  label: ReactNode
  value?: ReactNode
  /** Linha de apoio abaixo do valor (ex.: CNPJ do laboratório). Só aparece se houver valor. */
  detail?: ReactNode
  /** Ocupa duas colunas (textos longos). */
  wide?: boolean
  /** Texto quando não há valor. */
  emptyLabel?: string
}

export const DetailField: FC<DetailFieldProps> = ({
  label,
  value,
  detail,
  wide,
  emptyLabel = 'Não informado',
}) => {
  const hasValue = value !== undefined && value !== null && value !== ''

  return (
    <div className={clsx(styles.field, wide && styles.fieldWide)}>
      <dt className={styles.label}>{label}</dt>
      <dd className={styles.valueGroup}>
        <span className={clsx(styles.value, !hasValue && styles.valueMuted)}>
          {hasValue ? value : emptyLabel}
        </span>
        {hasValue && detail && <span className={styles.detail}>{detail}</span>}
      </dd>
    </div>
  )
}

/** Lista de características sim/não (✓ / ✕), com rótulo. */
export const DetailFeatures: FC<{ label: ReactNode; children: ReactNode }> = ({
  label,
  children,
}) => {
  const labelId = useId()

  return (
    <div className={styles.featuresGroup}>
      <span className={styles.label} id={labelId}>
        {label}
      </span>
      <ul className={styles.features} aria-labelledby={labelId}>
        {children}
      </ul>
    </div>
  )
}

/**
 * Característica sim/não: o ícone muda de forma (✓ / ✕) e o texto perde peso quando não se
 * aplica — a cor nunca é o único sinal. O leitor de tela ouve "sim"/"não" depois do nome.
 */
export const DetailFeature: FC<{ label: ReactNode; active?: boolean }> = ({ label, active }) => (
  <li className={clsx(styles.feature, active ? styles.featureOn : styles.featureOff)}>
    <span className={styles.featureIcon} aria-hidden='true'>
      <KTSVG path={active ? IconPaths.Check : IconPaths.X} />
    </span>
    <span className={styles.featureLabel}>{label}</span>
    <span className='visually-hidden'>{active ? ': sim' : ': não'}</span>
  </li>
)

/** Rodapé discreto (ex.: "Cadastrado por …"). */
export const DetailFooter: FC<{ children: ReactNode }> = ({ children }) => (
  <div className={styles.footer}>{children}</div>
)
